import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { Cluster } from 'react-mapbox-gl';
import Map from '../../../map/components/Map';
import Route from '../../../map/components/route/Route';
import { MapGeometryExact } from '../../../map/models/MapGeometry';
import useStyles from './PatrolMap.styles';
import { RouteExact } from '../../../mission/models/Route';
import { WorkzoneExact } from '../../../mission/models/Workzone';
import { ObstacleExact } from '../../../mission/models/Obstacle';
import { FindingShape } from '../../../finding/models/Finding';
import { RightOfWayExact } from '../../../mission/models/RightOfWay';
import Coverage from '../../../map/components/coverage/Coverage';
import FlightPath from '../../../map/components/flightPath/FlightPath';
import Workzone from '../../../map/components/workzone/Workzone';
import UrbanArea from '../../../map/components/urbanArea/UrbanArea';
import { UrbanAreaExact } from '../../../mission/models/UrbanArea';
import { AirportExact } from '../../../mission/models/Airport';
import Airport from '../../../map/components/airport/Airport';
import { CoverageExact } from '../../../mission/models/Coverage';
import { UncoveredAreaExact } from '../../../mission/models/UncoveredArea';
import Obstacle from '../../../map/components/obstacle/Obstacle';
import { getBounds } from '../../../map/formatters/mapFormatter';
import Finding from '../../../map/components/finding/Finding';
import RightOfWay from '../../../map/components/rightOfWay/RightOfWay';
import UncoveredAreas from '../../../map/components/uncoveredArea/UncoveredAreas';
import { useFindingContext } from '../../../finding/contexts/findingContext';
import PipelineCenterline from '../../../map/components/pipelineCenterline/PipelineCenterline';
import { PipelineCenterlineExact } from '../../../mission/models/PipelineCenterline';
import HyspexCoverage from '../../../map/components/hyspexCoverage/HyspexCoverage';
import FindingCluster from '../../../map/components/finding/cluster/FindingCluster';

const getInitialBounds = (routes) => {
  const flattenedCoordinates = routes.reduce((coordinates, { geometry }) => [...coordinates, ...geometry.coordinates], []);
  return getBounds(flattenedCoordinates);
};

const [defaultZoom, maxZoomWithoutClusters] = [11, 14];

const PatrolMap = ({
  patrolId,
  isMissionShared,
  routes,
  coverage,
  hyspexCoverage,
  workzones,
  uncoveredAreas,
  urbanAreas,
  flightPath,
  rightOfWays,
  findings,
  obstacles,
  airports,
  pipelineCenterlines,
  centeredGeometries,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const [bounds, setBounds] = useState(getInitialBounds(routes));
  const [zoom, setZoom] = useState([defaultZoom]);
  const [center, setCenter] = useState();
  const [showRow, setShowRow] = useState(false);
  const [showRowText, setShowRowText] = useState('patrol.hiderow');
  const { clickedFinding, setClickedFinding } = useFindingContext();

  const handleMapClick = useCallback(() => setClickedFinding(undefined), [setClickedFinding]);
  const handleToggleRowVisibility = useCallback(() => {
    setShowRow(!showRow);
    if (showRow) {
      setShowRowText('patrol.hiderow');
    } else {
      setShowRowText('patrol.showrow');
    }
  }, [showRow, setShowRow, setShowRowText]);
  const handleManualZoomEnd = (_, event) => {
    if (event && event.target) setZoom([event.target.getZoom()]);
  };

  useEffect(() => {
    if (centeredGeometries && centeredGeometries.length > 0) {
      const flattenedCoordinates = centeredGeometries.reduce((coordinates, geometry) => [...coordinates, ...geometry.coordinates], []);
      setBounds(getBounds(flattenedCoordinates));
    }
  }, [centeredGeometries]);

  useEffect(() => {
    if (clickedFinding) {
      if (zoom[0] < maxZoomWithoutClusters) setZoom([maxZoomWithoutClusters]);
      setCenter([clickedFinding.coordinate.longitude, clickedFinding.coordinate.latitude]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only set zoom on clicked finding change. adding zoom as a dependency breaks the map
  }, [clickedFinding]);

  return (
    <div className={classes.root}>
      <div className={classes.rowToggle}>
        <Checkbox size="small" name="toggleRowCheck" id="toggleRowCheck" value={!showRow} onChange={handleToggleRowVisibility} />
        <label htmlFor="toggleRowCheck">{i18n.t(showRowText)}</label>
      </div>

      <Map initialBounds={bounds} center={center} onClick={handleMapClick} zoom={zoom} onManualZoomEnd={handleManualZoomEnd}>
        <>
          {routes.map(({ geometry, visible }, index) => (
            <Route
              key={String(index)}
              index={index}
              coordinatesArray={geometry.coordinates}
              visible={visible}
              options={{ color: theme.palette.common.black, width: 2 }}
            />
          ))}

          {showRow &&
            rightOfWays.map(({ geometry, visible }, index) => (
              <RightOfWay key={String(index)} index={index} coordinatesArray={geometry.coordinates} visible={visible} />
            ))}

          {/* There is a problem with the way we convert geometries containing holes in the backend */}
          {/* This way we can skip the conversion for the newer data while still supporting the older data */}

          {(() => {
            if (isMissionShared) {
              if (coverage && coverage[0] && coverage[0].geometry.originalGeometry) {
                const { type } = coverage[0].geometry.originalGeometry;
                const originalCoordinates = coverage[0].geometry.originalGeometry.coordinates;

                const coordinates =
                  type === 'Polygon'
                    ? originalCoordinates.map((ring) => ring.map((point) => [point[0], point[1]]))
                    : originalCoordinates.map((polygon) => polygon.map((ring) => ring.map((point) => [point[0], point[1]])));

                return (
                  <Coverage
                    key="a"
                    index={0}
                    geometry={{
                      type,
                      coordinates,
                    }}
                    visible={coverage[0].visible}
                  />
                );
              }
              return (coverage || []).map(({ geometry, visible }, index) => (
                <Coverage
                  key={String(index)}
                  index={index}
                  geometry={{
                    type: geometry.type,
                    coordinates: geometry.coordinates.map(({ longitude, latitude }) => [longitude, latitude]),
                  }}
                  visible={visible}
                />
              ));
            }
            return coverage && coverage[0] && coverage[0].geometry.originalGeometry ? (
              <Coverage key="a" index={0} geometry={coverage[0].geometry.originalGeometry} visible={coverage[0].visible} />
            ) : (
              (coverage || []).map(({ geometry, visible }, index) => (
                <Coverage
                  key={String(index)}
                  index={index}
                  geometry={{
                    type: geometry.type,
                    coordinates: geometry.coordinates.map(({ longitude, latitude }) => [longitude, latitude]),
                  }}
                  visible={visible}
                />
              ))
            );
          })()}

          {(() => {
            if (isMissionShared) {
              if (hyspexCoverage && hyspexCoverage[0] && hyspexCoverage[0].geometry.originalGeometry) {
                const { type } = hyspexCoverage[0].geometry.originalGeometry;
                const originalCoordinates = hyspexCoverage[0].geometry.originalGeometry.coordinates;

                const coordinates =
                  type === 'Polygon'
                    ? [originalCoordinates.map((ring) => ring.map((point) => [point[0], point[1]]))]
                    : originalCoordinates.map((polygon) => polygon.map((ring) => ring.map((point) => [point[0], point[1]])));

                return (
                  <HyspexCoverage
                    key="b"
                    index={0}
                    geometry={{
                      type,
                      coordinates,
                    }}
                    visible={hyspexCoverage[0].visible}
                  />
                );
              }
              return (hyspexCoverage || []).map(({ geometry, visible }, index) => (
                <HyspexCoverage
                  key={String(index)}
                  index={index}
                  geometry={{
                    type: geometry.type,
                    coordinates: geometry.coordinates.map(({ longitude, latitude }) => [longitude, latitude]),
                  }}
                  visible={visible}
                />
              ));
            }

            return hyspexCoverage && hyspexCoverage[0] && hyspexCoverage[0].geometry.originalGeometry ? (
              <HyspexCoverage key="b" index={0} geometry={hyspexCoverage[0].geometry.originalGeometry} visible={hyspexCoverage[0].visible} />
            ) : (
              (hyspexCoverage || []).map(({ geometry, visible }, index) => (
                <HyspexCoverage
                  key={String(index)}
                  index={index}
                  geometry={{
                    type: geometry.type,
                    coordinates: geometry.coordinates.map(({ longitude, latitude }) => [longitude, latitude]),
                  }}
                  visible={visible}
                />
              ))
            );
          })()}

          {flightPath && <FlightPath coordinatesArray={flightPath.coordinates} visible={flightPath.visible} />}

          {workzones.map(({ geometry, visible }, index) => (
            <Workzone key={String(index)} index={index} coordinatesArray={geometry.coordinates} visible={visible} />
          ))}

          {urbanAreas.map(({ geometry, visible }, index) => (
            <UrbanArea key={String(index)} index={index} coordinatesArray={geometry.coordinates} visible={visible} />
          ))}

          <Cluster
            ClusterMarkerFactory={(coordinates, pointCount) => (
              <FindingCluster key={`${coordinates[0]}${pointCount}`} coordinates={coordinates} pointCount={pointCount} />
            )}
            minZoom={5}
            maxZoom={13}>
            {findings.map((finding, index) => {
              const coordinates = [finding.coordinate.longitude, finding.coordinate.latitude];
              return <Finding key={String(index)} index={index} patrolId={patrolId} finding={finding} coordinates={coordinates} visible={finding.visible} />;
            })}
          </Cluster>

          {obstacles.map(({ geometry, visible }, index) => (
            <Obstacle key={String(index)} index={index} coordinate={geometry.coordinates[0]} visible={visible} />
          ))}

          {airports.map(({ geometry, visible }, index) => (
            <Airport key={String(index)} index={index} coordinate={geometry.coordinates[0]} visible={visible} />
          ))}

          {pipelineCenterlines.map(({ geometry, visible }, index) => (
            <PipelineCenterline key={String(index)} index={index} coordinatesArray={geometry.coordinates} visible={visible} />
          ))}

          <UncoveredAreas uncoveredAreas={uncoveredAreas} />
        </>
      </Map>
    </div>
  );
};

PatrolMap.propTypes = {
  patrolId: PropTypes.number.isRequired,
  airports: PropTypes.arrayOf(AirportExact),
  centeredGeometries: PropTypes.arrayOf(MapGeometryExact),
  coverage: PropTypes.arrayOf(CoverageExact),
  hyspexCoverage: PropTypes.arrayOf(CoverageExact),
  findings: PropTypes.arrayOf(FindingShape),
  flightPath: MapGeometryExact,
  obstacles: PropTypes.arrayOf(ObstacleExact),
  pipelineCenterlines: PropTypes.arrayOf(PipelineCenterlineExact),
  rightOfWays: PropTypes.arrayOf(RightOfWayExact).isRequired,
  routes: PropTypes.arrayOf(RouteExact).isRequired,
  uncoveredAreas: PropTypes.arrayOf(UncoveredAreaExact),
  urbanAreas: PropTypes.arrayOf(UrbanAreaExact),
  workzones: PropTypes.arrayOf(WorkzoneExact),
};

PatrolMap.defaultProps = {
  airports: [],
  centeredGeometries: undefined,
  coverage: [],
  hyspexCoverage: [],
  findings: [],
  flightPath: undefined,
  obstacles: [],
  pipelineCenterlines: [],
  uncoveredAreas: [],
  urbanAreas: [],
  workzones: [],
};

PatrolMap.propTypes = {
  isMissionShared: PropTypes.bool.isRequired,
};

export default PatrolMap;
